import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

@Injectable()
export class ErrorService {
  rateLimitError = 'An error occurred due to too many requests. Please wait a minute and try again.';

  constructor() {}

  /**
   * Attempt to get provider information based on the format of the message.
   * @param er HttpErrorResponse object to be examined.
   */
  getProvider(er: HttpErrorResponse) {
    let provider;
    if (er && er.error && er.error.error) {
      provider = 'novadine';
    } else if (er && er.error && er.error.num && er.error.message) {
      provider = 'olo';
    } else if (er && er.error && er.error.conflictingFields) {
      provider = 'paytronix';
    } else {
      provider = 'other';
    }
    return provider;
  }

  /**
   * Maps Olo error codes to user-friendly error messages
   * @param errorNum Olo error code number
   * @param errorMessage Original error message that may contain item-specific details
   * @returns User-friendly error message
   */
  mapOloErrorMessage(errorNum: number, errorMessage: string): string {
    switch (errorNum) {
      case 9:
        return 'The selected reward could not be found or is no longer available. Please try selecting a different reward.';
      case 200:
        // Special case: if message says "error validating basket", use generic message
        if (errorMessage?.toLowerCase().includes('error validating basket')) {
          return 'There was an error processing your order. Please try again or contact customer support for assistance.';
        }
        return errorMessage || 'There was an error processing your order. Please try again or contact customer support for assistance.';
      case 201:
        return 'The coupon you entered cannot be applied to your current order. Please try a different coupon or remove it to continue.';
      case 203:
        return 'Your payment was declined. Please try a different payment method to complete your order.';
      case 204:
        return 'Your payment was declined due to address verification. Please verify your billing address is correct and try again.';
      case 205:
        return 'Your payment was declined due to an invalid security code (CVV). Please check your card details and try again.';
      case 206:
        return 'Your requested pickup/delivery time is too soon. Please select a later time to allow for adequate preparation.';
      case 207:
        return "The app version you're using is no longer supported. Please update to the latest version to continue ordering.";
      case 208:
        return 'The reward you selected cannot be applied to your current order. Please try a different reward or remove it to continue.';
      case 209:
        return 'This order has already been submitted successfully. Please check your order history for details.';
      case 210:
        return "The restaurant doesn't have capacity for your requested time. Please select a different time to complete your order.";
      case 211:
        return 'One or more items in your order are currently unavailable. Please remove these items and try again.';
      case 212:
        return 'One or more items in your order are not available at your selected time. Please choose a different time or remove these items.';
      case 213:
        return 'There was an error sending your order to the restaurant. Please try again in a few moments.';
      case 215:
        return "Your payment method doesn't have sufficient funds to complete this order. Please try a different payment method.";
      case 216:
        return "Your reward couldn't be redeemed at this time. Please try a different reward or remove it to continue.";
      case 217:
        return "We can't reach the restaurant at the moment. Please try again in a few minutes or select a different location.";
      case 218:
        return 'The store is currently is currently closed, please select a time for your order before continuing.';
      case 219:
        return 'This restaurant is temporarily unavailable for online ordering. Please call the restaurant directly or choose another location.';
      case 220:
        return 'This restaurant is currently unavailable for online ordering. Please call the restaurant directly or choose another location.';
      case 221:
        return "There's an issue with the coupon you selected. Please remove it from your order and try again.";
      case 222:
        return 'There seems to be an issue with one or more items in your order. Please try removing items and adding them again.';
      case 223:
        return "The restaurant's system is currently busy. Please wait a moment and try again.";
      case 224:
        return 'One or more items in your saved order are not available at this restaurant. Please modify your order and try again.';
      case 225:
        return "The restaurant's system is taking too long to respond. Please try again in a few moments.";
      case 226:
        return "We couldn't find an account with your email address. Please verify your information or sign up.";
      case 227:
        return 'You need to reset your password. Please use the "Forgot Password" option to continue.';
      case 228:
        return 'There was an issue with your delivery options. Please verify your delivery address and try again.';
      case 250:
        return 'One or more items in your order are no longer on the menu. Please remove these items and try again.';
      case 251:
        return 'Some items in your order are missing required selections. Please complete all required options and try again.';
      case 252:
        return "Some items in your order don't meet the required selections. Please check your items and adjust accordingly.";
      case 253:
        return 'The promotion code you entered is invalid. Please double-check your code and try again.';
      case 254:
        return "Your saved card's security code (CVV) couldn't be verified. Please re-enter your complete card information and try again.";
      default:
        return errorMessage || 'There was an error processing your order. Please try again or contact customer support for assistance.';
    }
  }

  loginError(er: HttpErrorResponse): string {
    let erString = 'Error logging in. Please check your username and password and try again. If the issue persists, please contact us.';

    console.log('ERROR SERVICE PROCESSING:', JSON.stringify(er));

    // Handle rate limiting
    if (er.status === 429 || er.status === 0) {
      erString = this.rateLimitError;
    }

    const provider = this.getProvider(er);

    if ((provider === 'olo' || provider === 'punchh-olo') && er.status === 400) {
      if (er.error?.num) {
        erString = this.mapOloErrorMessage(er.error.num, er.error.message);
      } else {
        erString = er.error.message;
      }

      // Special handling for password reset
      if (er.error?.num === 227) {
        erString = 'You need to reset your password. Please use the "Forgot Password" option to continue.';
      }
    }

    if (er.status === 401) {
      // 401 means authentication error.
      erString =
        'Login unsuccessful. Please check your username and password before trying again. If the issue persists, please contact us.';
    } else if (er.status === 404) {
      // Not found, may be offline. Any vendor.
      erString = 'Cannot reach login server. Please try again momentarily.';
    }

    const isVerificationError =
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      (er.name && (er.name.includes('Email Verification') || er.name === 'Email Verification Required')) ||
      (er.message && er.message.toLowerCase().includes('verify your email'));

    if (isVerificationError) {
      console.log('ERROR SERVICE: Detected verification error');
      erString =
        er.message ||
        'Your account has been created, but you must verify your email before you can login. Please check your inbox for the verification email';
    }

    return erString;
  }

  registerError(er: HttpErrorResponse): string {
    let erString =
      'Error creating an account. There may already be an account with this information. In that case, try logging in. If the issue persists, please contact us.';

    // Handle rate limiting
    if (er.status === 429 || er.status === 0) {
      erString = this.rateLimitError;
    }

    const provider = this.getProvider(er);

    if ((provider === 'olo' || provider === 'punchh-olo') && er.error?.message) {
      if (er.error?.num) {
        erString = this.mapOloErrorMessage(er.error.num, er.error.message);
      } else {
        erString = er.error.message;
      }

      if (er.error?.num === 226) {
        erString = "We couldn't find an account with your email address. Please verify your information or sign up.";
      }
    }

    if (provider === 'paytronix') {
      if (er.error.result === 'uniquenessConflict') {
        erString =
          'Error creating an account, there is already an account with this information. The following are conflicting with an existing account: ';
        er.error.conflictingFields.forEach((field: string, index: number) => {
          console.log(field);
          if (field === 'mobilePhone') {
            erString = erString.concat('phone number');
          }
          if (field === 'email') {
            erString = erString.concat('email address');
          }
          if (er.error.conflictingFields.length - 1 > index) {
            erString = erString.concat(', ');
          }
        });
      }
    }

    if (er.status === 403 && er.error.code === 6) {
      // Olo-specific. The API key does not support registration. Which is bad.
      erString = 'Sorry, we are not able to accept new registrations at this time. Please order as a guest.';
    } else if (er.status === 404) {
      // Not found, may be offline. Any vendor.
      erString = 'Cannot reach login server. Please try again momentarily.';
    } else if (er.status === 422 && provider === 'other') {
      // Punchh uses this code for username/password already taken.
      erString = 'An account with this info already exists. Either sign in instead, or else use different info.';
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (er.name && (er.name.includes('Email Verification') || er.name === 'Email Verification Required')) {
      erString = er.message;
    }

    if (er.message?.includes('Please select a location to see your rewards')) {
      erString = '';
    }

    if (er.name.includes('Email Verification')) {
      erString = er.message;
    }

    return erString;
  }

  checkoutError(er: HttpErrorResponse): string {
    let erString =
      'There was an error submitting your order. Please check your details and try again. If the issue persists, please contact us.';

    // Handle rate limiting
    if (er.status === 429 || er.status === 0) {
      erString = this.rateLimitError;
    }

    const provider = this.getProvider(er);

    if (provider === 'olo' || provider === 'punchh-olo') {
      if (er.error?.num) {
        // For error codes 200 and 205, use the original error message from Olo
        if (er.error.num === 200 || er.error.num === 205) {
          // Special case: if error 200 says "error validating basket", use generic message
          if (er.error.num === 200 && er.error.message?.toLowerCase().includes('error validating basket')) {
            erString = this.mapOloErrorMessage(er.error.num, er.error.message);
          } else {
            erString = er.error.message + ' Please try again.';
          }
        } else {
          // Use the mapping function for all other Olo error codes
          erString = this.mapOloErrorMessage(er.error.num, er.error.message);
        }
      } else if (er.error?.message) {
        // Fallback to original error message if num is not available
        erString = er.error.message;

        if (er.error.message.toLowerCase().includes('no billing account specified')) {
          erString = 'Your payment method is either incomplete or missing. Please add a payment method and try again.';
        }
      }
    }

    if (er.status === 404) {
      // Not found, may be offline. Any vendor.
      erString = 'Cannot reach ordering server. Please try again momentarily.';
    }

    if (
      er.message &&
      (er.message === 'A duplicate transaction has been submitted.' || er.message === 'This transaction has been declined.')
    ) {
      erString =
        'There was an error submitting your order, your card may have been declined or the information submitted could be incorrect. Please check your details and try again. If the issue persists, please contact the store.';
    }

    return erString;
  }

  rewardApplyError(er: HttpErrorResponse): string {
    let erString = 'There was an error applying your reward. Please wait a minute and try again. If the issue persists please contact us.';

    // Handle rate limiting
    if (er.status === 429 || er.status === 0) {
      erString = this.rateLimitError;
    }

    const provider = this.getProvider(er);

    if (provider === 'olo' || provider === 'punchh-olo') {
      // Handle specific reward error codes
      if (er.error?.num === 9) {
        erString = 'The selected reward could not be found or is no longer available. Please try selecting a different reward.';
      } else if (er.error?.num) {
        // Use the mapping function for all other Olo error codes
        erString = this.mapOloErrorMessage(er.error.num, er.error.message);

        // Special handling for rewards-specific errors
        if (er.error.num === 208 || er.error.num === 216) {
          erString = 'This reward cannot be applied to your current order. Please try a different reward or contact customer support.';
        }
      } else if (er.error?.message) {
        erString = er.error.message;
      }
    }

    if (er.status === 404) {
      // Not found, may be offline. Any vendor.
      erString = 'Cannot reach ordering server. Please try again momentarily.';
    }

    return erString;
  }

  productError(er: HttpErrorResponse): string {
    let erString =
      typeof er === 'string' ? er : 'There was an error loading this product. Please try again. If the issue persists please contact us.';

    // Handle rate limiting
    if (er.status === 429 || er.status === 0) {
      erString = this.rateLimitError;
    }

    const provider = this.getProvider(er);

    if (provider === 'olo' || provider === 'punchh-olo') {
      if (er.error?.num) {
        // Use the mapping function for all Olo error codes
        erString = this.mapOloErrorMessage(er.error.num, er.error.message);

        // Special handling for product-specific errors
        if (er.error.num === 211 || er.error.num === 250) {
          erString = 'This item is currently unavailable. Please try a different item or check back later.';
        } else if (er.error.num === 251 || er.error.num === 252) {
          erString = 'Please make all required selections for this item before adding it to your order.';
        }
      } else if (er.error?.message) {
        erString = er.error.message;
      }
    }

    if (er.status === 404) {
      // Not found, may be offline. Any vendor.
      erString = 'Cannot reach ordering server. Please try again momentarily.';
    }

    return erString;
  }

  locationRewardsError(er: HttpErrorResponse): string {
    let erString = 'Please select a location to see your rewards';

    // Handle rate limiting specifically
    if (er.status === 429) {
      erString = 'An error occurred due to too many requests. Please wait a minute and try again.';
    }

    // Handle network connectivity issues
    else if (er.status === 0) {
      erString = 'Cannot reach rewards server. Please check your connection and try again.';
    }

    console.log(er.message);

    const provider = this.getProvider(er);

    if (provider === 'olo' || provider === 'punchh-olo') {
      if (er.error?.num) {
        // Use the mapping function for all Olo error codes
        erString = this.mapOloErrorMessage(er.error.num, er.error.message);

        // Special handling for location-specific errors
        if (er.error.num === 226) {
          erString =
            "We couldn't find rewards for your account at this location. Please verify your account or select a different location.";
        }
      } else if (er.error?.message) {
        erString = er.error.message;
      }
    }

    if (er.status === 404) {
      // Not found, may be offline. Any vendor.
      erString = 'Cannot reach ordering server. Please try again momentarily.';
    }

    if (er.message === 'Please select a location to see your rewards.') {
      // Not found, may be offline. Any vendor.
      erString = 'Please select a location to see your rewards.';
    }

    return erString;
  }

  promoCodeError(er: HttpErrorResponse): string {
    let erString =
      'There was an error applying your promo code. Please check the code and try again. If the issue persists please contact us.';

    // Handle rate limiting
    if (er.status === 429 || er.status === 0) {
      erString = this.rateLimitError;
    }

    const provider = this.getProvider(er);

    if (provider === 'olo' || provider === 'punchh-olo') {
      if (er.error?.num) {
        // Use the mapping function for Olo error codes
        erString = this.mapOloErrorMessage(er.error.num, er.error.message);

        // Special handling for promo code-specific errors
        if (er.error.num === 201) {
          erString =
            'There was an error applying your promo code. Please check the code and try again. If the issue persists please contact us.';
        } else if (er.error.num === 221) {
          erString = 'There was an issue with the promo code. The code may be expired or invalid.';
        } else if (er.error.num === 253) {
          erString = 'The promo code you entered is invalid. Please double-check your code and try again.';
        }
      } else if (er.error?.message) {
        // Extract more specific error messages when available
        if (er.error.message.toLowerCase().includes('invalid')) {
          erString = 'The promo code you entered is invalid. Please check and try again.';
        } else if (er.error.message.toLowerCase().includes('expired')) {
          erString = 'This promo code has expired. Please try a different code.';
        } else if (er.error.message.toLowerCase().includes('minimum')) {
          erString = 'Your order does not meet the minimum requirements for this promo code.';
        } else {
          erString = er.error.message;
        }
      }
    } else if (provider === 'novadine') {
      // Specific handling for novadine errors
      if (er.error?.error?.toLowerCase().includes('invalid')) {
        erString = 'The promo code you entered is invalid or has expired. Please try a different code.';
      } else if (er.error?.error) {
        erString = er.error.error;
      }
    }

    if (er.status === 404) {
      // Not found, may be offline. Any vendor.
      erString = 'Cannot reach the server. Please try again momentarily.';
    }

    return erString;
  }
}
